import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { EMPTY, of, Subject } from "rxjs";
import { catchError, debounceTime, switchMap, takeUntil } from "rxjs/operators";
import { BaseComponent, ContentService, LearnContentType, UserService } from "@mypxplat/xplat/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-learn-content-picker",
  templateUrl: "learn-content-picker.component.html",
  standalone: false,
})
export class LearnContentPickerComponent extends BaseComponent implements OnChanges {
  @Input() type: LearnContentType = "video";
  @Input() excludeIds: any[] = [];
  @Input() hint = "";
  @Input() placeholder = "";
  @Input() disabled = false;
  @Input() admin = false;
  @Input() showDone = false;
  @Output() added = new EventEmitter<any>();

  public associationQuery = "";
  public associationResults: Array<any> = [];
  public searching = false;
  public hasSearchResults = false;
  public hasNoSearchResults = false;
  public searchEmptyMessage = "No videos found.";
  public isVideoType = true;
  public isLessonType = false;
  private query$ = new Subject<string>();

  constructor(
    userService: UserService,
    public contentService: ContentService
  ) {
    super(userService);
  }

  ngOnInit() {
    this.syncTypeFlags();
    this.query$
      .pipe(
        debounceTime(300),
        switchMap((query) => this.search(query)),
        takeUntil(this.destroy$)
      )
      .subscribe((items) => this.applySearchResults(items));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.type) {
      this.syncTypeFlags();
      if (!changes.type.firstChange) this.clear();
    }
  }

  onQueryInput() {
    const query = (this.associationQuery || "").trim();
    if (!query) {
      this.clear(false);
      this.query$.next("");
      return;
    }
    this.searching = true;
    this.hasSearchResults = false;
    this.hasNoSearchResults = false;
    this.associationResults = [];
    this.query$.next(query);
  }

  search(query: string) {
    if (!query) return EMPTY;
    return this.contentService.searchLearnContentForPicker(this.type, query, this.excludeIds || []).pipe(catchError(() => of([])));
  }

  applySearchResults(items: any[]) {
    this.searching = false;
    if (!(this.associationQuery || "").trim()) {
      this.clear(false);
      return;
    }
    this.associationResults = items;
    this.hasSearchResults = items.length > 0;
    this.hasNoSearchResults = items.length === 0;
  }

  clear(clearQuery = true) {
    if (clearQuery) this.associationQuery = "";
    this.searching = false;
    this.hasSearchResults = false;
    this.hasNoSearchResults = false;
    this.associationResults = [];
  }

  removeItem(id) {
    this.associationResults = this.associationResults.filter((item) => item.id != id);
    this.hasSearchResults = this.associationResults.length > 0;
    this.hasNoSearchResults = this.associationResults.length === 0;
  }

  onAdded(item) {
    this.added.emit(item);
  }

  private syncTypeFlags() {
    this.isVideoType = this.type === "video";
    this.isLessonType = this.type === "lesson";
    this.searchEmptyMessage = this.isVideoType ? "No videos found." : "No lessons found.";
  }
}
